import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Paragraph2, SmallText2 } from 'inpock-ui-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import marquee from 'vanilla-marquee';

import { thumbnail } from '@/utils';
import { Skeleton } from '@/components';

import * as S from './styled';

interface MarketHeaderProps {
  info: {
    username?: string;
    notice: string;
    images?: string[];
    loading?: boolean;
    logoText?: string;
    logoImage?: string;
    logoType?: 'text' | 'image';
    cartCount?: number;
  };
  version?: string;
}

function MarketHeaderSkeleton() {
  return (
    <>
      <S.SkeletonLogo>
        <Skeleton width={150} height={30} />
      </S.SkeletonLogo>
      <Skeleton height={375} />
    </>
  );
}

const MARQUEE_SPEED = {
  threshold: 50,
  value: [10, 10],
};

export function MarketHeader({ info, version }: MarketHeaderProps) {
  const navigate = useNavigate();
  const { username, notice, images, loading, logoText, logoImage, logoType, cartCount } = info;
  const { REACT_APP_LINK_CLIENT_URL } = process.env;

  const marqueeRef = useRef<HTMLDivElement>(null);
  const imageSectionRef = useRef<HTMLDivElement & HTMLImageElement>(null);

  const [hidden, setHidden] = useState<boolean>(false);
  const [prevScrollY, setPrevScrollY] = useState<number>(0);
  const [imageSectionBottom, setImageSectionBottom] = useState<number>(0);

  const onClickCart = () => navigate('/cart');

  const onClickCustomerMenu = () =>
    (window.location.href = `${REACT_APP_LINK_CLIENT_URL}/customer/login`);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    const isScrollingUp = currentScrollY < prevScrollY;

    setHidden(
      currentScrollY > imageSectionBottom && currentScrollY > prevScrollY && !isScrollingUp,
    );
    setPrevScrollY(currentScrollY);
  };

  useEffect(() => {
    if (imageSectionRef.current) {
      const rect = imageSectionRef.current.getBoundingClientRect();
      setImageSectionBottom(rect.bottom + window.scrollY);
    }
  }, [images]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollY]);

  useEffect(() => {
    if (marqueeRef.current) {
      const animation = new marquee(marqueeRef.current, {
        direction: 'left',
        duplicated: true,
        gap: 120,
        recalcResize: true,
        delayBeforeStart: 0,
        speed:
          notice?.length > MARQUEE_SPEED.threshold
            ? MARQUEE_SPEED.value[1]
            : MARQUEE_SPEED.value[0],
        startVisible: true,
      });

      animation.refresh();
    }
  }, [loading, notice]);

  if (loading) return <MarketHeaderSkeleton />;

  return (
    <>
      <S.MarketHeader>
        {/* 공지사항 */}
        {notice && (
          <S.MarketNotice>
            <div className="translucent-layer" />
            <div ref={marqueeRef} className="marquee-container" style={{ overflow: 'hidden' }}>
              {new Array(20).fill(notice).map((item, index) => (
                <Paragraph2
                  key={index}
                  className="text-bold text-white"
                  style={{ display: 'inline', marginLeft: '120px' }}
                >
                  {item}
                </Paragraph2>
              ))}
            </div>
          </S.MarketNotice>
        )}
        {/* 헤더 영역 */}
        <S.MarketTitle className={hidden ? 'hidden' : ''}>
          <div className="market-title">
            {logoType === 'text'
              ? logoText && <S.MarketLogoText>{logoText}</S.MarketLogoText>
              : logoImage && (
                  <S.MarketLogoImage src={thumbnail.small(logoImage)} alt="market-logo" />
                )}
          </div>
          <div className="d-flex" style={{ gap: '16px' }}>
            {version === 'v2' && (
              <S.BtnCartContainer onClick={onClickCart}>
                <S.BtnCart />
                <S.CartCount>
                  <SmallText2 className="text-white">{cartCount}</SmallText2>
                </S.CartCount>
              </S.BtnCartContainer>
            )}
            <S.BtnMenu onClick={onClickCustomerMenu} />
          </div>
        </S.MarketTitle>
      </S.MarketHeader>
      {/* 이미지 section */}
      {images && images.length > 0 && (
        <div style={{ position: 'relative', zIndex: '1' }} ref={imageSectionRef}>
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            navigation
            pagination={{
              el: '.swiper-pagination',
              type: 'fraction',
            }}
            loop
            autoplay={{ delay: 3000 }}
            modules={[Autoplay, Pagination]}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <S.MarketImage
                  src={thumbnail.origin(image)}
                  alt={`${username}-market-image-${index}`}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <S.SwiperPagination className="swiper-pagination" />
        </div>
      )}
    </>
  );
}
